import React from 'react';

import HorizontalScrollRow from 'components/SaloonLobby/HorizontalScrollRow';
import type { LobbyCardSize } from 'components/SaloonLobby/lobbyCardSize';
import { formatPrize } from 'components/SaloonLobby/stakesLabel';
import type { SitGo } from 'components/SaloonLobby/types';

import styles from './SitGoLadder.module.scss';

/** One prize pool and the tables that share it, ascending as SaloonLobby groups them. */
export type SitGoRung = readonly [prize: number, games: SitGo[]];

interface SitGoLadderProps {
  rungs: ReadonlyArray<SitGoRung>;
  /** The lobby's Card Size. A rung is exactly one tile wide, so the size picks the track. */
  size: LobbyCardSize;
  renderTile: (game: SitGo) => React.ReactNode;
}

/**
 * The Sit & Go ladder — the tab's layout (owner, 2026-09-10; out from behind
 * the Lobby Lab switch and into the lower environments 2026-09-11).
 *
 * Production's Sit & Go list is a handful of prize pools with one table each —
 * two when the first fills, rarely more. The cash tab's row-per-stake scroller
 * left 84% of every row empty at 1280 and pushed three of the five pools below
 * the fold. Here each prize pool is a COLUMN — the Tournament tab's day-column
 * anatomy — in a grid that centres as a block and WRAPS: when the viewport can
 * no longer hold every column, the last one drops under the first row, whose
 * height is its tallest column, so a pool with three tables pushes the wrapped
 * row below all three. Nothing scrolls sideways; the space below is the space
 * we have (owner, round 2). On phones the columns become stacked sections and a
 * pool's tables run in a carousel, the open table first.
 *
 * The tile never changes: one of the lobby's two fixed widths, whatever the
 * layout around it. Layout only — the tile is the card's (GameTile/, EN-10719).
 */
const SitGoLadder: React.FC<SitGoLadderProps> = ({ rungs, size, renderTile }) => (
  <div
    className={`${styles.ladder} ${
      size === 'compact' ? styles.ladderCompact : styles.ladderLarge
    }`}>
    {rungs.map(([prize, games]) => (
      // A named GROUP, not a named section. A named `<section>` is a `region`
      // landmark, and five to eight per tab — each named with text visible
      // right below it — is landmark noise no other tab here makes. But
      // dropping the name outright costs the player moving tile to tile by
      // Tab: the pool figure is on no TILE either (`renderSitGoTile` passes
      // the buy-in and the seats, never the pool), so they would hear
      // "$6 buy-in, 1 of 6" with nothing to tell a $30 pool from a $120 one.
      // `role="group"` + a name is announced on focus entry and never reaches
      // the landmarks rotor — the idiom YourTables already uses when its
      // visible label drops (YourTables.tsx:1746). Round 2, 2026-09-13.
      //
      // Heading navigation is the other half, and it landed lobby-wide in
      // EN-10751: the pool figure below is an <h2> under the tab's <h1>. The
      // two do different jobs and both stay — the heading is how the rotor
      // finds the $30 pool, the group is what a player hears on entering it
      // while moving tile to tile by Tab.
      <section
        key={prize}
        className={styles.rung}
        data-rung={prize}
        role='group'
        aria-label={`${formatPrize(prize)} prize pool`}>
        <div className={styles.rungHeader}>
          <h2 className={styles.rungPool}>{formatPrize(prize)}</h2>
          <span className={styles.rungKicker}>prize pool</span>
        </div>
        <HorizontalScrollRow className={styles.rungList}>
          {games.map(renderTile)}
        </HorizontalScrollRow>
      </section>
    ))}
  </div>
);

export default SitGoLadder;
